import { useEffect, useRef, useState } from 'react';
import { isValidSubdomain } from '@xsscool/shared';
import { useStore } from '../store';

export function DomainSwitcher() {
  const base = useStore((s) => s.domainBase);
  const sub = useStore((s) => s.domainSub);
  const available = useStore((s) => s.availableDomains);
  const setBase = useStore((s) => s.setDomainBase);
  const setSub = useStore((s) => s.setDomainSub);

  const [open, setOpen] = useState(false);
  const [subDraft, setSubDraft] = useState(sub);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => setSubDraft(sub), [sub]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const host = sub ? `${sub}.${base}` : base;

  const subInvalid = subDraft.length > 0 && !isValidSubdomain(subDraft);

  const commitSub = () => {
    if (subDraft.length === 0) {
      setSub('');
    } else if (isValidSubdomain(subDraft)) {
      setSub(subDraft);
    }
  };

  return (
    <div className="domain-switcher" ref={ref}>
      <button
        type="button"
        className="domain-trigger"
        onClick={() => setOpen((v) => !v)}
        title="Choose the domain that generated URLs target"
        aria-expanded={open}
      >
        <span className="domain-trigger-host">{host}</span>
        <span className="domain-trigger-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="domain-popover" role="dialog" aria-label="Domain switcher">
          <label className="domain-row">
            <span className="domain-label">Base</span>
            <select
              value={base}
              onChange={(e) => setBase(e.target.value)}
              disabled={available.length < 2}
            >
              {available.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label className="domain-row">
            <span className="domain-label">Subdomain</span>
            <input
              type="text"
              placeholder="(none)"
              value={subDraft}
              onChange={(e) => setSubDraft(e.target.value.trim().toLowerCase())}
              onBlur={commitSub}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitSub();
                  setOpen(false);
                }
              }}
              spellCheck={false}
              aria-invalid={subInvalid || undefined}
            />
          </label>
          {subInvalid && (
            <p className="domain-hint domain-hint-error">
              Single DNS label only: a–z, 0–9, hyphens.
            </p>
          )}
          <p className="domain-hint">
            Shares and captures are reachable on every host. The selection only
            changes the URLs the editor prints.
          </p>
        </div>
      )}
    </div>
  );
}
